import {
  ConeGeometry,
  DoubleSide,
  Euler,
  Mesh,
  Quaternion,
  Shape,
  ShapeGeometry,
  ShaderMaterial,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import {
  NO_PICK_LAYER,
  QuaternionTuple,
} from '@technology/web3d-plugin-threejs';
import {
  partialPointToVector3,
  degreesToRadians,
  arePointsEqual,
  areQuaternionsEqual,
  Point2D,
  Point3D,
} from '@trimble/utilities';
import { Accessor, createEffect, createSignal, Setter } from 'solid-js';
import { CapabilityCategory } from './base/models/capability-category';
import { SingleCapabilityBase } from './base/single-capability-base';
import { ThreeJsManager } from '@trimble/utilities';
import { calculateDistanceBasedScale } from '../helpers/distance-based-scale-calculation';
import {
  MAP_STATION_FAR_BOUNDARY,
  MAP_STATION_MARKER_COLOR,
  MAP_STATION_MARKER_OUTER_RING_COLOR,
  MAP_STATION_NEAR_BOUNDARY,
  MAP_STATION_OUTER_RING_UV_DIST,
  MAP_STATION_PLANE_SIZE,
  MAP_STATION_POINT_SIZE,
} from './constants/map-station';
import { FRUSTUM_MESH_TYPE, type MeshType } from '@trimble/types';

const FRUSTUM_POINT_SIZE = 25;
const NEAR_BOUNDARY = 300;
const FAR_BOUNDARY = 301;
const DISTANCE_THRESHOLD = 0.01;
const FRUSTUM_HEIGHT = 1;
const FRUSTUM_OUTER_RING_RADIUS = 1.8;
const FRUSTUM_POSITION_THRESHOLD = 0.01;
const FRUSTUM_OUTER_RING_OPACITY = 0.9;
const CIRCLE_OUTER_RING_RATIO = 0.7; // Ratio of innerRadius for the circle's outer ring
const FRUSTUM_INNER_RADIUS_MULTIPLIER = 1.3; // Makes the frustum circle larger than the station marker
const FRUSTUM_MESH_RENDER_ORDER = 3;
const FLAT_FRUSTUM_LENGTH_MULTIPLIER = 0.85;
const HALF = 0.5;
const CONE_HEIGHT_MULTIPLIER = 0.6;
const CONE_RADIAL_SEGMENTS = 36;
// eslint-disable-next-line no-magic-numbers
const LIGHT_DIRECTION: [number, number, number] = [0.2, 0.45, 1.0];
const MAX_CONE_HEIGHT = 2;
const RIM_POWER = 2.0;
const TIP_BOOST = 0.25;
const MIN_OUTER_RADIUS_PADDING = 0.05;
const MIN_HORIZONTAL_FOV_SCALE = 0.25;
const HALF_PI = Math.PI * HALF;

const CONE_VERTEX_SHADER = `
  varying float vHeightFactor;
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  uniform float height;
  void main() {
    vHeightFactor = position.y + 0.5;
    vNormal = normalize(normalMatrix * normal);
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = mvPosition.xyz;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const CONE_FRAGMENT_SHADER = `
  uniform vec3 originColor;
  uniform vec3 edgeColor;
  uniform float originAlpha;
  uniform float edgeAlpha;
  uniform vec3 lightDir;
  uniform float ambientStrength;
  uniform float diffuseStrength;
  uniform float rimStrength;
  uniform float rimPower;
  uniform float tipBoost;
  varying float vHeightFactor;
  varying vec3 vNormal;
  varying vec3 vViewPosition;

  void main() {
    float alpha = mix(edgeAlpha, originAlpha, vHeightFactor);
    vec3 baseColor = mix(edgeColor, originColor, vHeightFactor);

    vec3 n = normalize(vNormal);
    vec3 l = normalize(lightDir);
    float diffuse = max(dot(n, l), 0.0);
    float lighting = ambientStrength + diffuseStrength * diffuse;

    vec3 viewDir = normalize(-vViewPosition);
    float rim = pow(1.0 - max(dot(n, viewDir), 0.0), rimPower);
    vec3 litColor = baseColor * lighting + rimStrength * rim;

    float tipEmphasis = 1.0 + tipBoost * vHeightFactor;
    gl_FragColor = vec4(litColor * tipEmphasis, alpha);
  }
`;

export class FrustumCapability extends SingleCapabilityBase {
  public override category: CapabilityCategory = CapabilityCategory.Command;

  /**
   * Persistent user override of frustum visibility, independent of any
   * auto-show logic owned by a coordinator.
   *
   * Tri-state:
   * - `undefined` → follow the coordinator's auto-show conditions.
   * - `true`      → force shown, regardless of auto-show.
   * - `false`     → force hidden, regardless of auto-show.
   *
   * Combined as `shouldBeActive = userIntent() ?? autoShow` so the dropdown
   * can record a sticky preference and saved views can be restored
   * deterministically by writing the saved boolean.
   */
  public readonly userIntent: Accessor<boolean | undefined>;
  private readonly setUserIntentSignal: Setter<boolean | undefined>;

  private frustumMesh?: Mesh;
  private hasListener = false;
  private readonly useMapHeight: boolean;
  private readonly renderStyle: 'flat' | 'cone';

  private currentPosition: Vector3 | null = null;
  private currentQuaternion: QuaternionTuple | null = null;
  private currentFov: number | null = null;
  private currentScale: number | null = null;
  private currentDistance = 0;

  private cameraPosition: Vector3 | null = null;
  private cameraQuaternion: QuaternionTuple | null = null;
  private cameraProjectionMatrix: number[] | null = null;
  private frustumFrame: number | null = null;

  private readonly frustumMeshUserDataType: MeshType;

  private readonly updateCameraListener = (): void => {
    const projectionMatrix = getProjectionMatrix(this.viewer.camera);
    const projectionChanged =
      !this.cameraProjectionMatrix ||
      !projectionMatrix ||
      !areMatrixElementsEqual(this.cameraProjectionMatrix, projectionMatrix);

    if (
      !projectionChanged &&
      arePointsEqual(this.cameraPosition, this.viewer.camera.position) &&
      areQuaternionsEqual(this.cameraQuaternion, [
        this.viewer.camera.quaternion.x,
        this.viewer.camera.quaternion.y,
        this.viewer.camera.quaternion.z,
        this.viewer.camera.quaternion.w,
      ])
    ) {
      // Even if position/rotation hasn't changed, update scale if distance changed
      this.updateScale();
      return;
    }

    this.setFrustumPosition({
      targetPosition: this.currentPosition,
      quaternion: this.currentQuaternion,
      fov: this.currentFov,
    });

    this.cameraPosition = this.viewer.camera.position.clone();
    this.cameraQuaternion = this.viewer.camera.quaternion.clone().toArray();
    this.cameraProjectionMatrix = projectionMatrix
      ? [...projectionMatrix]
      : null;
  };

  constructor(
    viewer: Web3DViewer,
    private readonly threeJsManager: ThreeJsManager,
    options?: {
      useMapHeight?: boolean;
      renderStyle?: 'flat' | 'cone';
      /** Defaults to {@link FRUSTUM_MESH_TYPE} for the pane's own frustum. */
      meshUserDataType?: MeshType;
    }
  ) {
    super(viewer);
    this.useMapHeight = options?.useMapHeight ?? true;
    this.renderStyle = options?.renderStyle ?? 'flat';
    this.frustumMeshUserDataType =
      options?.meshUserDataType ?? FRUSTUM_MESH_TYPE;

    const [userIntent, setUserIntent] = createSignal<boolean | undefined>(
      undefined
    );
    this.userIntent = userIntent;
    this.setUserIntentSignal = setUserIntent;

    createEffect(() => {
      if (this.isActive()) {
        this.addCameraListener();

        if (
          this.currentPosition &&
          this.currentQuaternion &&
          this.currentFov &&
          this.currentScale
        ) {
          this.createFrustum();
        }
      } else {
        void this.removeCameraListener();
        this.unloadFrustum();
      }
    });
  }

  public get position(): Vector3 | null {
    return this.currentPosition;
  }

  /** Record the user's persistent toggle preference. See {@link userIntent}. */
  public setUserIntent(value: boolean): void {
    this.setUserIntentSignal(value);
  }

  public override async cleanup(): Promise<void> {
    await this.removeCameraListener();
    this.unloadFrustum();

    this.currentPosition = null;
    this.currentQuaternion = null;
    this.currentFov = null;
    this.currentScale = null;
    this.cameraPosition = null;
    this.cameraQuaternion = null;
    this.cameraProjectionMatrix = null;
    this.clearPendingFrustumUpdate();

    return Promise.resolve();
  }

  private addCameraListener(): void {
    if (this.hasListener) return;
    this.threeJsManager.addAnimationFrameListener(this.updateCameraListener);
    this.hasListener = true;
  }

  private async removeCameraListener(): Promise<void> {
    if (!this.hasListener) return;
    this.hasListener = false;

    await this.threeJsManager.removeAnimationFrameListener(
      this.updateCameraListener
    );
  }

  private scheduleFrustumUpdate(): void {
    if (this.frustumFrame != null) {
      return;
    }
    this.frustumFrame = requestAnimationFrame(() => {
      this.frustumFrame = null;
      this.createFrustum();
    });
  }

  private clearPendingFrustumUpdate(): void {
    if (this.frustumFrame != null) {
      cancelAnimationFrame(this.frustumFrame);
      this.frustumFrame = null;
    }
  }

  private createFrustum(): void {
    if (
      !this.isActive() ||
      !this.currentPosition ||
      !this.currentQuaternion ||
      !this.currentFov ||
      !this.currentScale
    ) {
      return;
    }

    if (this.frustumMesh && this.renderStyle !== 'cone') {
      this.unloadFrustum();
    }

    const fovRadian = degreesToRadians(this.currentFov);

    const frustumScaleRadius = calculateFrustumScaleAndRadius(
      this.viewer.camera.position,
      this.currentPosition
    );

    if (frustumScaleRadius === null) return;

    const { frustumScale, innerRadius } = frustumScaleRadius;

    if (this.renderStyle === 'cone') {
      this.updateConeFrustum(frustumScale, fovRadian);
    } else {
      const horizontalFactor = this.getHorizontalFovScale();
      this.createFlatFrustum(
        frustumScale,
        innerRadius,
        fovRadian,
        horizontalFactor
      );
    }

    this.currentDistance = this.viewer.camera.position.distanceTo(
      this.currentPosition
    );
  }

  private updateConeFrustum(frustumScale: number, fovRadian: number): void {
    const rawHeight =
      FRUSTUM_OUTER_RING_RADIUS * frustumScale * CONE_HEIGHT_MULTIPLIER;
    const height = Math.min(rawHeight, MAX_CONE_HEIGHT);
    const baseRadius = Math.tan(fovRadian * HALF) * height;
    if (!this.frustumMesh) {
      const geometry = new ConeGeometry(1, 1, CONE_RADIAL_SEGMENTS, 1, true);
      const material = new ShaderMaterial({
        uniforms: {
          originColor: {
            value: [
              MAP_STATION_MARKER_OUTER_RING_COLOR.r,
              MAP_STATION_MARKER_OUTER_RING_COLOR.g,
              MAP_STATION_MARKER_OUTER_RING_COLOR.b,
            ],
          },
          edgeColor: {
            value: [
              MAP_STATION_MARKER_COLOR.r,
              MAP_STATION_MARKER_COLOR.g,
              MAP_STATION_MARKER_COLOR.b,
            ],
          },
          originAlpha: { value: 0.75 },
          edgeAlpha: { value: 0.18 },
          height: { value: height },
          lightDir: { value: LIGHT_DIRECTION },
          ambientStrength: { value: 0.35 },
          diffuseStrength: { value: 0.85 },
          rimStrength: { value: 0.35 },
          rimPower: { value: RIM_POWER },
          tipBoost: { value: TIP_BOOST },
        },
        vertexShader: CONE_VERTEX_SHADER,
        fragmentShader: CONE_FRAGMENT_SHADER,
        side: DoubleSide,
        transparent: true,
        depthWrite: false,
      });

      this.frustumMesh = new Mesh(geometry, material);
      this.frustumMesh.userData = {
        type: this.frustumMeshUserDataType,
        style: 'cone',
      };
      this.frustumMesh.renderOrder = FRUSTUM_MESH_RENDER_ORDER;
      this.frustumMesh.layers.set(NO_PICK_LAYER);
      this.threeJsManager.addObject(this.frustumMesh);
    }

    const quaternion = new Quaternion(...this.currentQuaternion!);
    const forward = new Vector3(0, 0, -1).applyQuaternion(quaternion);
    const offsetPosition = this.currentPosition!.clone().add(
      forward.multiplyScalar(height * HALF)
    );
    this.frustumMesh.position.set(
      offsetPosition.x,
      offsetPosition.y,
      offsetPosition.z
    );
    const baseRotation = new Quaternion().setFromEuler(
      new Euler(HALF_PI, 0, 0)
    );
    this.frustumMesh.quaternion.copy(quaternion.multiply(baseRotation));
    this.frustumMesh.scale.set(baseRadius, height, baseRadius);
    const material = this.frustumMesh.material as ShaderMaterial;
    if (material.uniforms?.['height']) {
      material.uniforms['height'].value = height;
    }
    this.frustumMesh.updateMatrixWorld(true);
  }

  private createFlatFrustum(
    frustumScale: number,
    innerRadius: number,
    fovRadian: number,
    horizontalFactor: number
  ): void {
    const segment = new Shape();

    const scaledOuterRadius =
      FRUSTUM_OUTER_RING_RADIUS *
      FLAT_FRUSTUM_LENGTH_MULTIPLIER *
      horizontalFactor;
    const minOuterRadius = innerRadius + MIN_OUTER_RADIUS_PADDING;
    const outerRadius = Math.max(scaledOuterRadius, minOuterRadius);

    // Calculate the angles for the FOV wedge
    const halfFov = fovRadian * HALF;
    const startAngle = HALF_PI - halfFov;
    const endAngle = HALF_PI + halfFov;

    // Create path: circle with wedge - draw as continuous non-overlapping shape
    // Start at 0 angle on inner circle (opposite side from wedge)
    const circleStartAngle = startAngle + Math.PI;
    const circleStartX = innerRadius * Math.cos(circleStartAngle);
    const circleStartY = innerRadius * Math.sin(circleStartAngle);
    segment.moveTo(circleStartX, circleStartY);

    // Arc from opposite side to the wedge start (covers most of circle)
    segment.absarc(0, 0, innerRadius, circleStartAngle, startAngle, false);

    // Now extend outward for wedge
    const outerStartX = outerRadius * Math.cos(startAngle);
    const outerStartY = outerRadius * Math.sin(startAngle);
    segment.lineTo(outerStartX, outerStartY);

    // Arc along outer radius of wedge
    segment.absarc(0, 0, outerRadius, startAngle, endAngle, false);

    // Line back to innerRadius at endAngle
    const innerEndX = innerRadius * Math.cos(endAngle);
    const innerEndY = innerRadius * Math.sin(endAngle);
    segment.lineTo(innerEndX, innerEndY);

    // Arc back along inner circle to start (completes the circle)
    segment.absarc(0, 0, innerRadius, endAngle, circleStartAngle, false);

    segment.closePath();

    const geometry = new ShapeGeometry(segment);

    // Calculate the circle's inner and outer ring radii
    const circleInnerRadius = innerRadius * CIRCLE_OUTER_RING_RATIO;

    // Create a shader material:
    // - Circle base (0 to innerRadius): marker color center with ring color outer ring
    // - Wedge (innerRadius to outerRadius): marker color fading to transparent
    const material = new ShaderMaterial({
      uniforms: {
        markerColor: {
          value: [
            MAP_STATION_MARKER_OUTER_RING_COLOR.r,
            MAP_STATION_MARKER_OUTER_RING_COLOR.g,
            MAP_STATION_MARKER_OUTER_RING_COLOR.b,
          ],
        },
        ringColor: {
          value: [
            MAP_STATION_MARKER_COLOR.r,
            MAP_STATION_MARKER_COLOR.g,
            MAP_STATION_MARKER_COLOR.b,
          ],
        },
        circleInnerRadius: { value: circleInnerRadius },
        innerRadius: { value: innerRadius },
        outerRadius: { value: outerRadius },
        opacity: { value: FRUSTUM_OUTER_RING_OPACITY },
        halfFov: { value: halfFov },
      },
      vertexShader: `
        varying vec2 vPosition;
        void main() {
          vPosition = position.xy;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        #define PI 3.14159265359

        uniform vec3 markerColor;
        uniform vec3 ringColor;
        uniform float circleInnerRadius;
        uniform float innerRadius;
        uniform float outerRadius;
        uniform float opacity;
        uniform float halfFov;
        varying vec2 vPosition;

        void main() {
          float dist = length(vPosition);

          // Fine anti-aliasing parameters for crisp but smooth edges
          float colorTransitionWidth = 0.015; // Narrower for crisper transition
          float edgeSoftnessPixel = fwidth(dist) * 2.0; // Pixel-perfect anti-aliasing

          // Crisp transition between marker color and ring color
          float colorTransition = smoothstep(
            circleInnerRadius - colorTransitionWidth,
            circleInnerRadius + colorTransitionWidth,
            dist
          );

          vec3 finalColor;
          float alpha;

          if (dist < innerRadius) {
            // Circle area: crisp blend between colors
            finalColor = mix(markerColor, ringColor, colorTransition);

            // Smooth anti-aliased edge at innerRadius boundary
            float circleMask = 1.0 - smoothstep(
              innerRadius - edgeSoftnessPixel,
              innerRadius,
              dist
            );
            alpha = opacity * mix(1.0, circleMask, 0.3);
          } else {
            // Wedge area: markerColor fading to transparent
            finalColor = markerColor;
            float fadeTransition = smoothstep(innerRadius, outerRadius, dist);
            alpha = mix(opacity, 0.1, fadeTransition);
          }

          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      side: DoubleSide,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    // Create a mesh and add it to the scene
    this.frustumMesh = new Mesh(geometry, material);
    this.frustumMesh.userData = { type: this.frustumMeshUserDataType };

    // Position the mesh
    this.frustumMesh.position.set(
      this.currentPosition!.x,
      this.currentPosition!.y,
      this.currentPosition!.z
    );

    // Extract heading from quaternion and apply rotation
    const quaternion = new Quaternion(...this.currentQuaternion!);
    const newEuler = new Euler().setFromQuaternion(quaternion, 'ZYX');
    const heading = newEuler.z;

    this.frustumMesh.rotation.set(0, 0, heading);

    // Use the frustum scale we already calculated (allows continuous scaling)
    this.frustumMesh.scale.setScalar(frustumScale);
    this.frustumMesh.updateMatrixWorld(true);

    this.frustumMesh.renderOrder = FRUSTUM_MESH_RENDER_ORDER;
    this.frustumMesh.layers.set(NO_PICK_LAYER);

    this.threeJsManager.addObject(this.frustumMesh);
  }

  private getHorizontalFovScale(): number {
    if (!this.currentQuaternion) {
      return 1;
    }
    const quaternion = new Quaternion(...this.currentQuaternion);
    const forward = new Vector3(0, 0, -1).applyQuaternion(quaternion);
    const horizontal = Math.sqrt(forward.x * forward.x + forward.y * forward.y);
    return Math.max(MIN_HORIZONTAL_FOV_SCALE, Math.min(horizontal, 1));
  }

  private updateScale(): void {
    if (!this.currentPosition) return;

    const distance = this.viewer.camera.position.distanceTo(
      this.currentPosition
    );

    // Only update if distance changed by more than threshold to avoid unnecessary updates
    if (Math.abs(distance - this.currentDistance) > DISTANCE_THRESHOLD) {
      this.scheduleFrustumUpdate();
    }
  }

  public unloadFrustum(): void {
    if (this.frustumMesh) {
      void this.threeJsManager.removeObject(this.frustumMesh);

      this.frustumMesh.geometry.dispose();

      if (Array.isArray(this.frustumMesh.material)) {
        this.frustumMesh.material.forEach((mat) => {
          mat.dispose();
        });
      } else {
        this.frustumMesh.material.dispose();
      }

      this.frustumMesh = undefined;
    }
  }

  // Frustum-specific positioning.
  public setFrustumPosition(data: {
    targetPosition: Point3D | Point2D | Vector3 | null;
    quaternion: QuaternionTuple | null;
    fov: number | null;
  }): void {
    const targetPosition = data.targetPosition
      ? partialPointToVector3(data.targetPosition)
      : null;

    const newPosition = targetPosition?.clone() ?? this.currentPosition;

    if (!newPosition) {
      return;
    }

    if (this.useMapHeight) {
      // Place the frustum at a fixed height above the ground
      newPosition.z = FRUSTUM_HEIGHT;
    }

    // If position hasn't changed significantly, don't recreate frustum
    // This prevents recreation during camera animation when PaneManager syncs
    if (this.currentPosition) {
      const distance = this.currentPosition.distanceTo(newPosition);

      if (distance > FRUSTUM_POSITION_THRESHOLD) {
        this.currentPosition = newPosition;
      }
    } else {
      this.currentPosition = newPosition;
    }

    this.currentQuaternion =
      data.quaternion ??
      this.currentQuaternion ??
      new Quaternion().setFromEuler(new Euler(0, 0, -HALF_PI)).toArray(); // Panorama starts at heading=-PI/2 (looking at +X)

    this.currentFov =
      data.fov ?? this.currentFov ?? this.viewer.camera.fieldOfView;

    // Scale is now calculated dynamically in createFrustum based on camera distance
    this.currentScale = 1; // Placeholder, actual scale is calculated in createFrustum

    this.scheduleFrustumUpdate();
  }
}

function calculateFrustumScaleAndRadius(
  cameraPosition: Vector3,
  targetPosition: Vector3
): { frustumScale: number; innerRadius: number } | null {
  const frustumScale = calculateDistanceBasedScale(
    cameraPosition,
    targetPosition,
    FRUSTUM_POINT_SIZE,
    NEAR_BOUNDARY,
    FAR_BOUNDARY
  );

  if (frustumScale === 0) return null;

  const mapStationScale = calculateDistanceBasedScale(
    cameraPosition,
    targetPosition,
    MAP_STATION_POINT_SIZE,
    MAP_STATION_NEAR_BOUNDARY,
    MAP_STATION_FAR_BOUNDARY
  );

  const innerRadius =
    MAP_STATION_OUTER_RING_UV_DIST *
    MAP_STATION_PLANE_SIZE *
    (mapStationScale / frustumScale) *
    FRUSTUM_INNER_RADIUS_MULTIPLIER;

  return { frustumScale, innerRadius };
}

function getProjectionMatrix(camera: Web3DViewer['camera']): number[] | null {
  const projection = (
    camera as unknown as { projectionMatrix?: { elements: number[] } }
  ).projectionMatrix;
  return projection?.elements ?? null;
}

function areMatrixElementsEqual(
  a: number[] | null,
  b: number[] | null
): boolean {
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  const epsilon = 1e-6;
  for (let i = 0; i < a.length; i += 1) {
    if (Math.abs(a[i] - b[i]) > epsilon) {
      return false;
    }
  }
  return true;
}
